/**
 * Primary sidebar navigation. Single source of truth for the Sidebar
 * component, the CommandPalette, and the Shift+Arrow tab-cycling hotkey
 * in Layout.
 *
 * `adminOnly` items are filtered out for non-admin users; non-admins
 * never see the link in the drawer or in the cmd+k palette.
 */
import { isAdminEmail } from '@/auth/admin';

export interface NavItem {
  to: string;
  label: string;
  adminOnly?: boolean;
  /**
   * Section the item belongs to. The Sidebar renders a small uppercase
   * header above the first item of each group. Ungrouped items (e.g. `docs`)
   * render with no header. The flat-list consumers (CommandPalette,
   * Layout's tab-cycling) ignore this field.
   */
  group?: string;
}

export const NAV_ITEMS: NavItem[] = [
  // Observe — the read/triage surfaces.
  { to: '/insights', label: 'insights', group: 'Observe' },
  { to: '/issues', label: 'issues', group: 'Observe' },
  { to: '/investigate', label: 'investigate', group: 'Observe' },
  { to: '/investigations', label: 'past', group: 'Observe' },
  // Memory is open to all authenticated users (read-only browse). Admin
  // gates only apply to write actions inside the page (promote / retire /
  // archive). Server enforces; FE disables the buttons for non-admins.
  { to: '/memory', label: 'memory', group: 'Observe' },
  // /reviews is the Sherlock Reviewer PR-review agent's list page. The
  // registry admin sub-route (/reviews/repos) is reached via a toolbar
  // button on that page, so it is intentionally NOT a nav item — same
  // pattern as /requests/new off /requests.
  { to: '/reviews', label: 'reviews', group: 'Observe' },
  // Deliver — the PM / requirements surfaces.
  // /scorecard is the KPI scorecard — all authenticated users, read-only.
  { to: '/scorecard', label: 'scorecard', group: 'Deliver' },
  // /requests is the list + analytics page; the capture wizard lives at the
  // /requests/new sub-route (reached via the "+ New request" button), so it
  // is intentionally NOT a nav item — tab-cycling and ⌘K skip it. Reads are
  // open to all; archive is admin-only inside the page.
  { to: '/requests', label: 'requests', group: 'Deliver' },
  { to: '/teams', label: 'teams', adminOnly: true, group: 'Deliver' },
  { to: '/notifications', label: 'notifications', adminOnly: true, group: 'Deliver' },
  // /integrations is the per-front-door kill switch + live counters page
  // (currently just the Slack adapter). Reads open to all; the switch and
  // Stop are admin-only, gated inside the page (same pattern as /teams).
  { to: '/integrations', label: 'integrations', adminOnly: true, group: 'Settings' },
  // Ungrouped — utility surfaces at the bottom.
  { to: '/docs', label: 'docs' },
];

/**
 * Return the NAV_ITEMS visible to the supplied user email — strips
 * admin-only entries when the caller is not the admin.
 */
export function visibleNavItems(email: string | null | undefined): NavItem[] {
  const isAdmin = isAdminEmail(email);
  return NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
}
